import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap'
import { Scissors, AlertCircle, ArrowRight, Layers, CheckCircle } from 'react-feather'
import { newFormatThousandSeparator } from 'utils/formatNumber'

/**
 * ModalConfirmAutoPopulate
 * Popup confirmation modal to inform user when Auto Populate requires splitting or modifying inventory.
 */
const ModalConfirmAutoPopulate = ({
  isOpen,
  toggle,
  isLoading,
  onConfirm,
  plan,
  neededAmount, // the material's Dibutuhkan; the split itself follows the (free) Alokasi
}) => {
  if (!plan) return null

  const {
    recipeName,
    requiredAmount,
    unit = 'Kg',
    targetInv,
    splitAmount,
    remainderAmount,
    intactInvs = [],
  } = plan

  return (
    <Modal
      isOpen={isOpen}
      toggle={isLoading ? undefined : toggle}
      centered
      autoFocus={false}
      style={{ maxWidth: '540px' }}
      className="modal-dialog-centered"
      data-testid="modal-confirm-auto-populate"
    >
      <ModalHeader
        toggle={isLoading ? undefined : toggle}
        className="border-bottom-0 pb-2 pt-3 px-3 px-md-4"
      >
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: '42px',
              height: '42px',
              backgroundColor: '#eff6ff',
              border: '1px solid #dbeafe',
            }}
          >
            <Scissors size={20} color="#2563eb" />
          </div>
          <div>
            <h5 className="modal-title fw-bold text-dark mb-0" style={{ fontSize: '1.05rem' }}>
              Konfirmasi Pemisahan Inventori
            </h5>
            <span className="text-muted" style={{ fontSize: '0.78rem' }}>
              Auto Populate akan membagi inventori untuk memenuhi kebutuhan bahan baku.
            </span>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="px-3 px-md-4 py-2">
        {/* Info Alert Box */}
        <div
          className="p-3 rounded-3 mb-3 d-flex align-items-start gap-2"
          style={{
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
          }}
        >
          <AlertCircle size={16} color="#64748b" className="mt-1 flex-shrink-0" />
          <div style={{ fontSize: '0.82rem', color: '#334155', lineHeight: '1.4' }}>
            Untuk memenuhi kebutuhan <strong>{newFormatThousandSeparator(neededAmount ?? requiredAmount, 3)} {unit}</strong> pada{' '}
            <strong>{recipeName}</strong>, sistem akan memisahkan (<em>split</em>) stok inventori yang tersedia menjadi 2 batch baru.
          </div>
        </div>

        {/* Intact inventories if any (in case of combination) */}
        {intactInvs && intactInvs.length > 0 && (
          <div className="mb-3">
            <div className="fw-bold text-dark mb-2 d-flex align-items-center gap-1" style={{ fontSize: '0.8rem' }}>
              <Layers size={14} color="#64748b" />
              Inventori Utuh yang Digunakan:
            </div>
            <div className="d-flex flex-column gap-2">
              {intactInvs.map((inv) => (
                <div
                  key={inv.id}
                  className="p-2 rounded-2 d-flex justify-content-between align-items-center"
                  style={{ backgroundColor: '#f1f5f9', fontSize: '0.8rem' }}
                >
                  <div>
                    <div className="fw-semibold text-dark">{inv?.item?.nameItem || inv?.nameItem || recipeName || '-'}</div>
                    <div className="text-muted">Lot ID: {inv?.lotCode || '-'}</div>
                  </div>
                  <span className="fw-bold text-primary">
                    {newFormatThousandSeparator(inv.amountNum || inv.amount, 3)} {inv.unit || unit}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Target Inventory to Split */}
        {targetInv && (
          <div className="border rounded-3 p-3 bg-white shadow-sm mb-2">
            <div className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.68rem', letterSpacing: '0.5px' }}>
              Inventori Asal yang Akan Di-Split:
            </div>
            <div className="d-flex justify-content-between align-items-start pb-2 border-bottom">
              <div>
                <div className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>
                  {targetInv?.item?.nameItem || targetInv?.nameItem || recipeName || '-'}
                </div>
                <div className="fw-semibold text-dark" style={{ fontSize: '0.85rem' }}>
                  Lot ID: {targetInv?.lotCode || '-'}
                </div>
                <div className="text-muted text-xs">
                  {targetInv.warehouseName || '-'}
                  {targetInv.groupLabel ? ` | ${targetInv.groupLabel}` : ''}
                </div>
              </div>
              <div className="fw-bold text-dark text-end" style={{ fontSize: '0.95rem' }}>
                {newFormatThousandSeparator(targetInv.amountNum || targetInv.amount, 3)} {targetInv.unit || unit}
              </div>
            </div>

            {/* Split Outcome Arrow */}
            <div className="mt-3">
              <div className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.68rem', letterSpacing: '0.5px' }}>
                Hasil Pemisahan:
              </div>

              <div className="d-flex flex-column gap-2">
                {/* 1. Allocated Piece */}
                <div
                  className="p-2 rounded-2 d-flex justify-content-between align-items-center"
                  style={{
                    backgroundColor: '#ecfdf5',
                    border: '1px solid #a7f3d0',
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <CheckCircle size={15} color="#059669" />
                    <div>
                      <div className="fw-bold" style={{ color: '#065f46', fontSize: '0.82rem' }}>
                        Alokasi untuk Pemetaan
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                        Akan langsung dipilih ke formulir ini
                      </div>
                    </div>
                  </div>
                  <div className="fw-bold text-end" style={{ color: '#059669', fontSize: '0.9rem' }}>
                    {newFormatThousandSeparator(splitAmount, 3)} {unit}
                  </div>
                </div>

                {/* 2. Remainder Piece */}
                <div
                  className="p-2 rounded-2 d-flex justify-content-between align-items-center"
                  style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <ArrowRight size={15} color="#64748b" />
                    <div>
                      <div className="fw-bold text-dark" style={{ fontSize: '0.82rem' }}>
                        Sisa Inventori Baru
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                        Tersimpan kembali di gudang inventori
                      </div>
                    </div>
                  </div>
                  <div className="fw-bold text-muted text-end" style={{ fontSize: '0.9rem' }}>
                    {newFormatThousandSeparator(remainderAmount, 3)} {unit}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter className="border-top p-3 px-md-4 gap-2">
        <Button
          outline
          color="secondary"
          disabled={isLoading}
          onClick={toggle}
          className="px-3"
          style={{ borderRadius: '8px', height: '38px', fontSize: '0.85rem' }}
        >
          Batal
        </Button>
        <Button
          color="primary"
          disabled={isLoading}
          onClick={onConfirm}
          className="px-4 fw-bold d-flex align-items-center"
          style={{ borderRadius: '8px', height: '38px', fontSize: '0.85rem' }}
          data-testid="confirm-auto-populate-btn"
        >
          {isLoading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Memproses Pemisahan...
            </>
          ) : (
            <>
              <Scissors size={14} className="me-2" />
              Lanjutkan & Pisahkan
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ModalConfirmAutoPopulate
